import {
  BaseModel,
  TargetableModel,
  useTargetedModel,
  Watchable,
} from '@cotera/client/v0/actions/framework';
import {
  EventWatchableImpl,
  MixedSourceWatchable,
  ReadonlyEventWatchable,
  ReadonlyWatchable,
  type ReconcileOptions,
} from '@cotera/client/v0/actions/framework/watchable';
import { makeMessages, type Message } from '@cotera/client/v0/chat/utils';
import type {
  InitialAction,
  InitialArtifactParams,
  MessageClass,
  ToolLoader,
  WorkspaceDomainLoader,
} from '@cotera/sdk/core';
import { noopWorkspaceDomainLoader } from '@cotera/sdk/core';
import {
  type ChatRow,
  type ChatMessageRow,
  type ChatActionRow,
  transformChat,
  transformMessages,
  transformActions,
} from '../../../hooks/use-chat-stream';
import {
  BroadcastEventType,
  type BroadcastEventPayloadMap,
  type SerializedConversationSnapshot,
  type ResourceUpdatedPayload,
  ChatStatusFrameOutput,
  type ChatCostTotals,
} from '@cotera/api';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import type { ChatSurface } from '@cotera/client/app/etc/metrics/coco-events';
import { deriveSeedArtifactSurface } from '../utils/chat-surface-seed';
import { atom } from 'jotai';
import { Assert } from '@cotera/utilities';
import {
  getStackFrameAggregateStatus,
  getLastErrorFromStackFrames,
} from '../utils/coco-stack-frame-status';
import { Conversation, Lazy } from '@cotera/sdk/core';
import { coco } from '@cotera/api';
import type { UIMessage } from 'ai';
import { WatchablesFactory } from '../../watchables/watchables-factory';
import type { ChatSubmissionParams } from '../utils/prompt-utils';

export type DeferredChatSeedContext = {
  initialArtifacts?: InitialArtifactParams[];
  initialToolKeys?: string[];
  initialActions?: InitialAction[];
  agentId?: string;
  displayTitle?: string;
};

export type ChatStatus = 'streaming' | 'ready' | 'error' | 'submitted';

export type ChatStatusSnapshot = {
  stackFrames: ChatStatusFrameOutput[];
  totals: ChatCostTotals | null;
};

export function convertToChatStatus(
  status: string
): 'streaming' | 'ready' | 'error' | 'submitted' {
  switch (status) {
    case 'submitted':
      return 'submitted';
    case 'ready':
      return 'ready';
    case 'streaming':
      return 'streaming';
    case 'error':
      return 'error';
    default:
      return 'ready';
  }
}

const DEFAULT_CONVERSATION_RECONCILE_MS = 10_000;

export function mergeOptimisticSentMessage(params: {
  conversation: Conversation | null;
  sentMessage: UIMessage | null;
}): Conversation | null {
  const { conversation, sentMessage } = params;
  if (
    conversation === null ||
    sentMessage === null ||
    sentMessage.id === undefined
  ) {
    return conversation;
  }

  const hasMessage = conversation
    .getAllMessages()
    .some((message) => message.id === sentMessage.id);
  if (hasMessage) {
    return conversation;
  }

  const optimisticConversation = conversation.clone();
  optimisticConversation.addMessage(sentMessage);
  return optimisticConversation;
}

export function shouldTreatNoStackFramesAsCompleted(
  conversation: Conversation | null
): boolean {
  const lastMessage = conversation?.getAllMessages().at(-1);
  if (lastMessage === undefined) {
    return false;
  }
  return lastMessage.role === 'assistant';
}

type CocoChatModelProps = {
  conversationId: string;
  orgId: string;
  initialAutoApprove?: boolean;
  initialPlanMode?: boolean;
  initialModel?: string;
  systemSuffix?: string;
  getToolLoader: () => ToolLoader;
  getWorkspaceDomainLoader?: () => WorkspaceDomainLoader;
  client: TenantedClient['resource']['chat'];
  /** When set, passed to chat backend so the new conversation is assigned to this folder. */
  folderId?: string;
  /** When set, conversation watchable polls full snapshot on this cadence (ms) to self-heal after missed events. Defaults to 5s. */
  reconcile?: ReconcileOptions;
  watchablesFactory: Pick<WatchablesFactory, 'makeStackFrameWrittenWatchable'>;
};

async function conversationFromSnapshot(
  snapshot: SerializedConversationSnapshot,
  orgId: string,
  conversationId: string,
  getToolLoader: () => ToolLoader,
  getWorkspaceDomainLoader: () => WorkspaceDomainLoader,
  parentLazyFromRoot?: Lazy<Conversation | null>
): Promise<Conversation> {
  const chat = transformChat(
    snapshot.chat as ChatRow,
    snapshot.chat.agent_id ?? null
  );
  const { messages, messagePositions } = transformMessages(
    snapshot.messages as ChatMessageRow[]
  );
  const actions = transformActions(snapshot.actions as ChatActionRow[]);
  // Use owner org (folder's org when in cross-org folder) for tool/credential context
  const ownerOrgId =
    snapshot.chat?.owner_org_id ?? snapshot.chat?.org_id ?? orgId;

  const selfHolder: { self: Conversation | null } = { self: null };
  const parentLazyForDescendants =
    parentLazyFromRoot ??
    new Lazy<Conversation | null>(async () => selfHolder.self);

  const childConversations =
    snapshot.children !== undefined && snapshot.children.length > 0
      ? await Promise.all(
          snapshot.children.map((childSnap) =>
            conversationFromSnapshot(
              childSnap,
              childSnap.chat?.owner_org_id ??
                childSnap.chat?.org_id ??
                ownerOrgId,
              childSnap.chat.id,
              getToolLoader,
              getWorkspaceDomainLoader,
              parentLazyForDescendants
            )
          )
        )
      : undefined;
  const self = await Conversation.fromChatMessagesAsync({
    orgId: ownerOrgId,
    conversationId,
    messages,
    chat,
    messagePositions,
    actions,
    toolLoader: getToolLoader(),
    workspaceDomainLoader: getWorkspaceDomainLoader(),
    children: childConversations,
    parent: parentLazyFromRoot,
  });
  selfHolder.self = self;
  if (parentLazyFromRoot === undefined) {
    parentLazyForDescendants.preload(self);
  }
  return self;
}

function mergeMessageDeltaIntoSnapshot(
  snapshot: SerializedConversationSnapshot,
  messageDelta: SerializedConversationSnapshot['messages'][number]
): SerializedConversationSnapshot {
  const { position } = messageDelta;
  const messages = [...snapshot.messages];
  if (position >= messages.length) {
    messages.push(messageDelta);
  } else {
    messages[position] = messageDelta;
  }
  return { ...snapshot, messages };
}

/**
 * Avoids a slower full-snapshot fetch overwriting a newer in-memory snapshot
 * built from streaming message deltas (concurrent refresh race).
 */
export function shouldReplaceCachedSnapshot(
  candidate: SerializedConversationSnapshot,
  current: SerializedConversationSnapshot | null
): boolean {
  if (current === null) {
    return true;
  }
  if (candidate.messages.length !== current.messages.length) {
    return candidate.messages.length >= current.messages.length;
  }
  const candidateLast = candidate.messages[candidate.messages.length - 1];
  const currentLast = current.messages[current.messages.length - 1];
  if (candidateLast === undefined || currentLast === undefined) {
    return true;
  }
  return candidateLast.updated_at >= currentLast.updated_at;
}

function assignCachedSnapshotIfNewer(
  ref: { current: SerializedConversationSnapshot | null },
  candidate: SerializedConversationSnapshot
): SerializedConversationSnapshot {
  if (shouldReplaceCachedSnapshot(candidate, ref.current)) {
    ref.current = candidate;
    return candidate;
  }
  return ref.current ?? candidate;
}

function mergeActionDeltaIntoSnapshot(
  snapshot: SerializedConversationSnapshot,
  actionDelta: SerializedConversationSnapshot['actions'][number]
): SerializedConversationSnapshot {
  const actions = [...snapshot.actions];
  const idx = actions.findIndex((a) => a.id === actionDelta.id);
  if (idx >= 0) {
    actions[idx] = actionDelta;
  } else {
    actions.push(actionDelta);
  }
  return { ...snapshot, actions };
}

/**
 * Whether a chat broadcast payload targets the watched conversation (directly
 * or as an ancestor of the chat that changed).
 */
export function chatBroadcastPayloadMatchesWatchedConversation(
  watchConversationId: string,
  payload: { chatId?: string; conversationAncestry?: string[] }
): boolean {
  if (payload.chatId === watchConversationId) {
    return true;
  }
  return payload.conversationAncestry?.includes(watchConversationId) === true;
}

/**
 * Whether an `artifact-created` payload may apply its augmented `conversation`
 * to the watched id. Snapshots are rooted at `chatId` (the chat that changed).
 */
export function artifactCreatedPayloadAppliesInlineConversation(
  watchConversationId: string,
  payload: {
    chatId: string;
    conversation?: SerializedConversationSnapshot;
  }
): boolean {
  return (
    payload.chatId === watchConversationId && payload.conversation !== undefined
  );
}

/**
 * Whether a chat `resource-updated` payload may apply augmented
 * `conversation` / `messageDelta` for the watched conversation. Those fields
 * are loaded for `resourceId`; parent watchables that match only via
 * `conversationAncestry` must refresh from the API instead.
 */
export function chatResourceUpdatedPayloadAppliesInlineConversationData(
  watchConversationId: string,
  p: ResourceUpdatedPayload
): boolean {
  if (p.resourceType !== 'chat') {
    return false;
  }
  if (p.conversationAncestry === undefined) {
    return false;
  }
  if (
    p.resourceId !== watchConversationId &&
    !p.conversationAncestry.includes(watchConversationId)
  ) {
    return false;
  }
  return p.resourceId === watchConversationId;
}

type ChatResourceMessageSyncPayload = Extract<
  ResourceUpdatedPayload,
  { resourceType: 'chat' }
>;
type ChatActionUpdatedPayload =
  BroadcastEventPayloadMap[typeof BroadcastEventType.ChatActionUpdated];
type ArtifactCreatedPayload =
  BroadcastEventPayloadMap[typeof BroadcastEventType.ArtifactCreated];

type ConversationEventPayload =
  | ChatResourceMessageSyncPayload
  | ChatActionUpdatedPayload
  | ArtifactCreatedPayload;

type ConversationEventWatchableDeps = {
  conversationId: string;
  orgId: string;
  getToolLoader: () => ToolLoader;
  getWorkspaceDomainLoader: () => WorkspaceDomainLoader;
  client: TenantedClient['resource']['chat'];
  /** When set, polls fn(undefined) on this cadence (ms) to self-heal after missed events. */
  reconcile?: ReconcileOptions;
};

/** Keep showing the last loaded conversation when reconcile polls fail (e.g. transient 401). */
export function preserveConversationOnSnapshotFetchFailure(
  previousConversation: Conversation | null
): Conversation | null {
  return previousConversation;
}

class ConversationEventWatchable extends EventWatchableImpl<
  Conversation | null,
  ConversationEventPayload
> {
  constructor(deps: ConversationEventWatchableDeps) {
    const lastSnapshotRef: {
      current: SerializedConversationSnapshot | null;
    } = { current: null };
    const lastConversationRef: { current: Conversation | null } = {
      current: null,
    };

    const {
      conversationId,
      orgId,
      getToolLoader,
      getWorkspaceDomainLoader,
      client,
      reconcile,
    } = deps;

    const toConversation = async (
      snapshot: SerializedConversationSnapshot
    ): Promise<Conversation> => {
      const conversation = await conversationFromSnapshot(
        snapshot,
        orgId,
        conversationId,
        getToolLoader,
        getWorkspaceDomainLoader
      );
      lastConversationRef.current = conversation;
      return conversation;
    };

    const fetchSnapshotOrPreserve = async (): Promise<Conversation | null> => {
      const res = await client.getConversationSnapshot({ conversationId });
      if (res.isErr()) {
        return preserveConversationOnSnapshotFetchFailure(
          lastConversationRef.current
        );
      }
      const snapshot = assignCachedSnapshotIfNewer(
        lastSnapshotRef,
        res.value.conversation as SerializedConversationSnapshot
      );
      return toConversation(snapshot);
    };

    super(() => atom<Conversation | null>(null), {
      events: [
        BroadcastEventType.ResourceUpdated,
        BroadcastEventType.ChatActionUpdated,
        BroadcastEventType.ArtifactCreated,
      ],
      runOnMount: true,
      initialValue: null,
      reconcile,
      debounceMs: 5,
      serializeRefreshes: true,
      serializeQueueStrategy: 'discard',
      fn: async (payload, _get) => {
        if ((window as any)['DEBUG_CONVERSATION_EVENT_WATCHABLE'] === true) {
          //eslint-disable-next-line no-console
          console.log('ConversationEventWatchable', payload);
        }
        if (payload === undefined || payload === null) {
          return fetchSnapshotOrPreserve();
        }

        if (
          'action' in payload &&
          payload.action !== undefined &&
          payload.chatId === conversationId
        ) {
          const last = lastSnapshotRef.current;
          if (last === null) {
            const res = await client.getConversationSnapshot({
              conversationId,
            });
            if (res.isErr()) {
              return preserveConversationOnSnapshotFetchFailure(
                lastConversationRef.current
              );
            }
            const snapshot = res.value
              .conversation as SerializedConversationSnapshot;
            lastSnapshotRef.current = snapshot;
            const merged = mergeActionDeltaIntoSnapshot(
              snapshot,
              payload.action
            );
            lastSnapshotRef.current = merged;
            return toConversation(merged);
          }
          const merged = mergeActionDeltaIntoSnapshot(last, payload.action);
          lastSnapshotRef.current = merged;
          return toConversation(merged);
        }

        const artifactPayload = payload as ArtifactCreatedPayload;
        if (
          artifactCreatedPayloadAppliesInlineConversation(
            conversationId,
            artifactPayload
          )
        ) {
          lastSnapshotRef.current = artifactPayload.conversation!;
          return toConversation(artifactPayload.conversation!);
        }

        const resourcePayload = payload as ResourceUpdatedPayload;
        if (
          chatResourceUpdatedPayloadAppliesInlineConversationData(
            conversationId,
            resourcePayload
          )
        ) {
          const chatPayload = resourcePayload as ChatResourceMessageSyncPayload;
          if (chatPayload.conversation !== undefined) {
            lastSnapshotRef.current = chatPayload.conversation;
            return toConversation(chatPayload.conversation);
          }
          if (
            chatPayload.messageDelta !== undefined &&
            chatPayload.messagePosition !== undefined
          ) {
            const { messageDelta, messagePosition } = chatPayload;
            const last = lastSnapshotRef.current;
            if (last === null || messagePosition > last.messages.length) {
              const res = await client.getConversationSnapshot({
                conversationId,
              });
              if (res.isErr()) {
                return preserveConversationOnSnapshotFetchFailure(
                  lastConversationRef.current
                );
              }
              const fetched = res.value
                .conversation as SerializedConversationSnapshot;
              const withDelta = mergeMessageDeltaIntoSnapshot(
                fetched,
                messageDelta
              );
              const snapshot = assignCachedSnapshotIfNewer(
                lastSnapshotRef,
                withDelta
              );
              return toConversation(snapshot);
            }
            const merged = mergeMessageDeltaIntoSnapshot(last, messageDelta);
            lastSnapshotRef.current = merged;
            return toConversation(merged);
          }
        }

        return fetchSnapshotOrPreserve();
      },
      filter: (payload) => {
        if (payload === undefined || payload === null) {
          return false;
        }
        if (
          'chatId' in payload &&
          chatBroadcastPayloadMatchesWatchedConversation(conversationId, {
            chatId: payload.chatId,
            conversationAncestry:
              'conversationAncestry' in payload
                ? payload.conversationAncestry
                : undefined,
          })
        ) {
          return true;
        }
        if (
          'resourceType' in payload &&
          payload.resourceType === 'chat' &&
          payload.conversationAncestry !== undefined
        ) {
          const ru = payload as ChatResourceMessageSyncPayload;
          const ancestry = ru.conversationAncestry;
          if (ancestry === undefined) {
            return false;
          }
          return (
            ru.resourceId === conversationId ||
            ancestry.includes(conversationId)
          );
        }
        return false;
      },
    });
  }
}

export class CocoChatModel extends BaseModel implements TargetableModel {
  readonly resourceType = 'chat' as const;
  private _deferredSeedContext: DeferredChatSeedContext | null = null;
  /**
   * The agent/workflow surface this chat was seeded with. Persistent — survives
   * the deferred-context clearing on first submit — so analytics report the
   * correct surface even before the seed is applied to the conversation.
   */
  private _seededSurface: ChatSurface | null = null;
  private _pendingToolCallResults: Record<string, unknown> = {};
  private _isTargeted: boolean = false;
  private unsubscribe: (() => void)[] = [];
  private _streamingCheckTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private _setInputContent:
    | ((value: {
        text?: string;
        editorState?: { type: 'doc'; content?: unknown[] };
      }) => void)
    | null = null;
  private _dispose: () => void;

  readonly autoApproveToolsWatchable: Watchable<boolean>;
  /** Whether plan mode is on for this conversation (architecture skills + plan-first behavior). */
  readonly planModeWatchable: Watchable<boolean>;
  readonly conversationWatchable: MixedSourceWatchable<Conversation | null>;
  readonly sentMessageWatchable: Watchable<UIMessage | null>;
  /**
   * True between the user clicking "stop" and the next message submission.
   * Consumed by UI (thinking dots, stack-frame header) to hide "in progress"
   * affordances immediately on cancel, without waiting for server-side stack
   * frame events to arrive marking frames as cancelled.
   */
  readonly cancelledWatchable: Watchable<boolean>;
  readonly loadedToolsWatchable: ReadonlyWatchable<string[]>;
  readonly inputWatchable: Watchable<string>;
  /**
   * Empty↔non-empty flip of the composer draft, derived from
   * {@link inputWatchable}. Subscribers (the send/mic button, the footer's
   * inline voice button) wake only when this boolean changes — not on every
   * keystroke — because jotai bails on `Object.is`.
   */
  readonly hasInputWatchable: ReadonlyWatchable<boolean>;
  readonly hasMessages: ReadonlyWatchable<boolean>;
  readonly messagesWatchable: ReadonlyWatchable<MessageClass[]>;
  readonly messagesStatusWatchable: ReadonlyWatchable<'loading' | 'ready'>;
  readonly statusWatchable: MixedSourceWatchable<ChatStatus>;
  readonly errorWatchable: MixedSourceWatchable<Error | undefined>;
  /** Raw chatStatus response stream — subscribe to {@link stackFramesWatchable} or {@link costTotalsWatchable} for specific fields; use this directly to call `refresh()` or `unsubscribe()`. */
  readonly chatStatusWatchable: ReadonlyEventWatchable<ChatStatusSnapshot>;
  readonly stackFramesWatchable: ReadonlyWatchable<ChatStatusFrameOutput[]>;
  readonly costTotalsWatchable: ReadonlyWatchable<ChatCostTotals | null>;
  /** Client-only title until the server conversation provides a persisted title. */
  readonly seededDisplayTitleWatchable: Watchable<string | null>;
  /**
   * Optimistic title applied immediately after a rename, before the server
   * conversation snapshot round-trips. Takes precedence over the persisted
   * title so the breadcrumb/selection update without waiting for a refetch.
   */
  readonly optimisticTitleWatchable: Watchable<string | null>;

  readonly conversationId: string;
  readonly systemSuffix: string | undefined;
  /** When set, passed to chat backend so the new conversation is assigned to this folder. */
  readonly folderId: string | undefined;
  private readonly _model: string;
  /**
   * The model the user explicitly chose this session (a new-chat seed or the
   * model switcher), or `undefined` when no explicit choice was made — e.g. a
   * follow-up on an existing chat. Only the explicit value is sent to the
   * backend so follow-ups don't overwrite the conversation's persisted model
   * (which, for an agent-run chat, is the agent's model).
   */
  private readonly _explicitModel: string | undefined;

  constructor(props: CocoChatModelProps) {
    super(props.conversationId);

    const {
      conversationId,
      orgId,
      initialAutoApprove,
      initialPlanMode,
      initialModel,
      systemSuffix,
      getToolLoader,
      getWorkspaceDomainLoader = () => noopWorkspaceDomainLoader,
      client,
      folderId,
      reconcile,
      watchablesFactory,
    } = props;

    this._isTargeted = true;
    this.systemSuffix = systemSuffix;
    this.folderId = folderId;
    this._explicitModel =
      initialModel !== undefined && initialModel.trim().length > 0
        ? initialModel.trim()
        : undefined;
    this._model = this._explicitModel ?? coco.DEFAULT_MODEL;
    this.autoApproveToolsWatchable = Watchable.fromValue(
      initialAutoApprove ?? false
    );
    this.planModeWatchable = Watchable.fromValue(initialPlanMode ?? false);
    this.inputWatchable = Watchable.fromValue('');
    this.hasInputWatchable = this.inputWatchable.map(
      (v) => v.trim().length > 0
    );
    this.seededDisplayTitleWatchable = Watchable.fromValue<string | null>(null);
    this.optimisticTitleWatchable = Watchable.fromValue<string | null>(null);
    this.sentMessageWatchable = Watchable.fromValue<UIMessage | null>(null);
    this.cancelledWatchable = Watchable.fromValue<boolean>(false);
    this.conversationId = conversationId;

    type StackFrameWrittenPayload =
      BroadcastEventPayloadMap[typeof BroadcastEventType.StackFrameWritten];

    const conversationWatchable = new ConversationEventWatchable({
      conversationId,
      orgId,
      getToolLoader,
      getWorkspaceDomainLoader,
      client,
      reconcile: reconcile ?? { cadence: DEFAULT_CONVERSATION_RECONCILE_MS },
    });

    const eventWatchablesFactory = watchablesFactory;

    const chatStatusWatchable =
      eventWatchablesFactory.makeStackFrameWrittenWatchable<
        ChatStatusSnapshot,
        StackFrameWrittenPayload
      >({
        runOnMount: true,
        initialValue: { stackFrames: [], totals: null },
        reconcile: reconcile ?? { cadence: DEFAULT_CONVERSATION_RECONCILE_MS },
        fn: async (payload, _get) => {
          if (payload !== undefined && payload !== null) {
            if (
              payload.conversationId === conversationId &&
              payload.stackFrames !== undefined
            ) {
              return {
                stackFrames: payload.stackFrames,
                totals: payload.totals ?? null,
              };
            }
            if (
              payload.parentConversationId === conversationId &&
              payload.parentStackFrames !== undefined
            ) {
              return {
                stackFrames: payload.parentStackFrames,
                totals: payload.parentTotals ?? null,
              };
            }
          }
          const res = Assert.assertOk(
            await client.chatStatus({ conversationId })
          );
          return { stackFrames: res.stackFrames, totals: res.totals };
        },
        filter: (payload) =>
          payload?.conversationAncestry?.includes(conversationId) ?? false,
        fallback: {
          fn: async () => {
            const res = Assert.assertOk(
              await client.chatStatus({ conversationId })
            );
            return { stackFrames: res.stackFrames, totals: res.totals };
          },
          interval: 10_000,
        },
      });

    this.conversationWatchable = MixedSourceWatchable.from((get) => {
      const baseConversation = get(conversationWatchable);
      const sentMessage = get(this.sentMessageWatchable);
      return mergeOptimisticSentMessage({
        conversation: baseConversation,
        sentMessage,
      });
    });
    this.loadedToolsWatchable = Watchable.from((get) => {
      const conversation = get(this.conversationWatchable);
      return conversation?.getLoadedTools().map((t) => t.toString()) ?? [];
    });
    this.chatStatusWatchable = chatStatusWatchable;
    this.stackFramesWatchable = chatStatusWatchable.map((s) => s.stackFrames);
    this.costTotalsWatchable = chatStatusWatchable.map((s) => s.totals);
    this.hasMessages = Watchable.from((get) => {
      const conversation = get(this.conversationWatchable);
      return (conversation?.getAllMessages().length ?? 0) > 0;
    });
    this.messagesWatchable = Watchable.from((get) => {
      const conversation = get(this.conversationWatchable);
      return conversation?.getAllMessages() ?? [];
    });
    this.messagesStatusWatchable = Watchable.from((get) => {
      const raw = get(this.conversationWatchable);
      return raw === null ? 'loading' : 'ready';
    });
    this.errorWatchable = MixedSourceWatchable.from((get) => {
      const { stackFrames } = get(chatStatusWatchable);
      const lastError = getLastErrorFromStackFrames(stackFrames);
      return lastError !== undefined ? new Error(lastError) : undefined;
    });
    this.statusWatchable = MixedSourceWatchable.from((get) => {
      const conversation = get(this.conversationWatchable);
      const cancelled = get(this.cancelledWatchable);
      const { stackFrames } = get(chatStatusWatchable);
      const derivedStatus = getStackFrameAggregateStatus(stackFrames);

      // Async tools (workflows, sub-agents) keep the turn open after the
      // parent run's stack frames complete: the tool part stays incomplete
      // until the child finishes and resumes the parent. Skipped after a local
      // cancel so stop settles to ready before server-side cancellation lands.
      const hasIncompleteToolCalls = !(
        conversation?.shouldYieldControlBackToTheLlm() ?? true
      );
      if (hasIncompleteToolCalls && !cancelled) {
        return 'streaming';
      }

      // Stack frames from chatStatus are authoritative once present: cancellation
      // completes the run even if tool parts are still marked incomplete locally.
      if (stackFrames.length > 0) {
        return convertToChatStatus(derivedStatus);
      }

      return 'ready';
    });

    this._dispose = () => {
      conversationWatchable.unsubscribe();
      chatStatusWatchable.unsubscribe();
    };

    const unsubSeededTitle = this.conversationWatchable.subscribe(
      (conversation: Conversation | null) => {
        const title = conversation?.title();
        if (title !== undefined && title.trim() !== '') {
          this.seededDisplayTitleWatchable.set(null);
        }
      }
    );
    this.unsubscribe.push(unsubSeededTitle);

    if (initialAutoApprove === undefined) {
      let hasSyncedFromConversation = false;
      const unsub = this.conversationWatchable.subscribe(
        (conversation: Conversation | null) => {
          if (
            !hasSyncedFromConversation &&
            conversation !== null &&
            conversation !== undefined
          ) {
            hasSyncedFromConversation = true;
            void conversation.mode().then((mode) => {
              this.autoApproveToolsWatchable.set(mode === 'auto_approve');
            });
            unsub();
          }
        }
      );
      this.unsubscribe.push(unsub);
    }

    // Sync plan mode from the persisted conversation on first hydration so it
    // survives reload. Skipped when the caller seeds plan mode explicitly.
    if (initialPlanMode === undefined) {
      let hasSyncedPlanMode = false;
      const unsubPlanMode = this.conversationWatchable.subscribe(
        (conversation: Conversation | null) => {
          if (
            !hasSyncedPlanMode &&
            conversation !== null &&
            conversation !== undefined
          ) {
            hasSyncedPlanMode = true;
            this.planModeWatchable.set(conversation.planMode);
            unsubPlanMode();
          }
        }
      );
      this.unsubscribe.push(unsubPlanMode);
    }
  }

  dispose() {
    if (this._streamingCheckTimeoutId !== null) {
      clearTimeout(this._streamingCheckTimeoutId);
      this._streamingCheckTimeoutId = null;
    }
    this.unsubscribe.forEach((unsubscribe) => unsubscribe());
    this.unsubscribe = [];
    this._dispose();
  }

  get messages(): Message[] {
    const messageInstances = this.messagesWatchable.snapshot();
    const rawMessages = messageInstances.map((m: MessageClass) => m.raw());
    return makeMessages(rawMessages);
  }

  /**
   * Id of the last message, without the O(N·P) `makeMessages` rebuild `messages`
   * incurs. `makeMessages` is a 1:1 map so this equals `messages.at(-1)?.id`.
   * Read this in render paths (e.g. the reasoning "is streaming" check) instead
   * of `messages`.
   */
  get lastMessageId(): string | undefined {
    return this.messagesWatchable.snapshot().at(-1)?.id;
  }

  get status(): 'ready' | 'streaming' | 'submitted' | 'error' {
    return convertToChatStatus(this.statusWatchable.snapshot());
  }

  get model(): string {
    return this._model;
  }

  /**
   * The user's explicit model choice for this session, or `undefined` when
   * none was made. Sent to the backend only when defined so a follow-up
   * doesn't overwrite the conversation's persisted model. See {@link _explicitModel}.
   */
  get explicitModel(): string | undefined {
    return this._explicitModel;
  }

  hasIncompleteToolCalls() {
    return this.messages.some(
      (x: Message) => (x.toolInvocations?.incomplete().length ?? 0) > 0
    );
  }

  get isTargeted() {
    return this._isTargeted;
  }

  target(): void {
    this._isTargeted = true;
  }

  markAsNotTargeted(): void {
    this._isTargeted = false;
  }

  addToolResult(toolCallId: string, result: unknown) {
    this._pendingToolCallResults[toolCallId] = result;
  }

  registerInputContentSetter(
    setter: (value: {
      text?: string;
      editorState?: { type: 'doc'; content?: unknown[] };
    }) => void
  ): () => void {
    this._setInputContent = setter;
    return () => {
      if (this._setInputContent === setter) {
        this._setInputContent = null;
      }
    };
  }

  setInputValue(value: string): void {
    this.inputWatchable.set(value);
    this._setInputContent?.({ text: value });
  }

  setInputEditorState(value: { type: 'doc'; content?: unknown[] }): void {
    this._setInputContent?.({ editorState: value });
  }

  setOptimisticallySentMessage(message: UIMessage): void {
    this.sentMessageWatchable.set(message);
  }

  /** Optimistically reflect a rename until the server snapshot catches up. */
  setOptimisticTitle(title: string): void {
    this.optimisticTitleWatchable.set(title);
  }

  hasDeferredSeedContext(): boolean {
    return this._deferredSeedContext !== null;
  }

  getDeferredSeedArtifacts(): InitialArtifactParams[] | undefined {
    return this._deferredSeedContext?.initialArtifacts;
  }

  setDeferredSeedContext(context: DeferredChatSeedContext): void {
    this._deferredSeedContext = context;
    const seededSurface = deriveSeedArtifactSurface(context);
    if (seededSurface !== null) {
      this._seededSurface = seededSurface;
    }
    if (context.displayTitle !== undefined && context.displayTitle !== '') {
      this.seededDisplayTitleWatchable.set(context.displayTitle);
    }
  }

  /** The agent/workflow surface this chat was seeded with, or null. */
  get seededSurface(): ChatSurface | null {
    return this._seededSurface;
  }

  mergeDeferredSeedIntoSubmitParams<T extends ChatSubmissionParams>(
    params: T
  ): T {
    if (this._deferredSeedContext === null) {
      return params;
    }

    const deferred = this._deferredSeedContext;
    this._deferredSeedContext = null;

    return {
      ...params,
      initialArtifacts: params.initialArtifacts ?? deferred.initialArtifacts,
      initialToolKeys: params.initialToolKeys ?? deferred.initialToolKeys,
      initialActions: params.initialActions ?? deferred.initialActions,
      agentId: params.agentId ?? deferred.agentId,
    };
  }

  clearOptimisticallySentMessage(): void {
    this.sentMessageWatchable.set(null);
  }

  clearPendingToolResults() {
    this._pendingToolCallResults = {};
  }

  async clearChat() {
    this.clearPendingToolResults();
    this.setInputValue('');
  }

  get pendingToolCallResults() {
    return this._pendingToolCallResults;
  }

  isEqualTo(other: CocoChatModel) {
    return this.id === other.id;
  }
}

export const useCocoChatModel = () => {
  const model = useTargetedModel(CocoChatModel);
  if (model === null) {
    throw new Error('CocoModel not found in scope');
  }
  return model;
};
